// SCR-STAFF-853 (AP-INS-02؛ US-INS-060 تا 064): میز کار مطالبات — صف آماده‌سازی با کنترل کیفیت هشت‌بررسی، دسته‌های دوره و
// ارسال طبق روش قرارداد، و صف نواقص به تفکیک مسئول رفع. قلم دارای نقص در دسته می‌نشیند ولی ارسال آن فقط با تأیید صریح
// «ارسال با ریسک» ممکن است (BR-INS-062)، و قلم نزدیک مهلت ارسال با نشان «در خطر انقضا» برجسته می‌شود (AC-INS-063.2).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type InsClaimBatchView,
  type InsClaimDefectInfo,
  type InsClaimItemView,
  type InsClaimQualityView,
  type InsClaimDefectOwner,
  type InsInsurerView,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  CheckboxRHF,
  DataTable,
  faDigits,
  FormDialog,
  JalaliDate,
  JalaliDatePickerRHF,
  ListPage,
  Money,
  SelectRHF,
  StatusChip,
  TextFieldRHF,
  type DataTableColumn,
} from '@phoenix/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import { insuranceKeys } from './common';
import {
  batchTone,
  ClaimDefects,
  claimKeys,
  claimTone,
  CLAIM_STATUSES,
  DEDUCTION_REASONS,
  DefectOwner,
  DEFECT_OWNERS,
  type ClaimStatus,
  type DeductionReason,
} from './claims';

export const SCR_STAFF_853 = 'SCR-STAFF-853';

export function ClaimsWorkbenchPage() {
  const { t } = useTranslation('ins');
  const access = useAccess();
  const write = useWriteAccess('ins.claim.manage');
  const { tenantApi, session } = useTenantSession();
  const [tab, setTab] = useState<'queue' | 'batches' | 'defects'>('queue');
  const [insurerId, setInsurerId] = useState('');
  const canManage = access.can('ins.claim.manage') && write.canWrite;

  const insurers = useQuery({
    queryKey: insuranceKeys.insurers(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/insurance/insurers', { params: { query: { limit: 200 } } })),
  });

  return (
    <ListPage
      screenId={SCR_STAFF_853}
      titleKey="ins:claims.title"
      subtitleKey="ins:claims.subtitle"
      filters={
        <Stack spacing={1}>
          <WriteNotice write={write} />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <TextField
              select
              size="small"
              label={t('ins:claims.insurer')}
              value={insurerId}
              onChange={(event) => setInsurerId(event.target.value)}
              sx={{ minWidth: 200 }}
              data-testid="ins-claims-insurer"
            >
              <MenuItem value="">{t('ins:claims.all')}</MenuItem>
              {(insurers.data?.items ?? []).map((insurer: InsInsurerView) => (
                <MenuItem key={insurer.insurerId} value={insurer.insurerId}>
                  {insurer.name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <Tabs value={tab} onChange={(_, value: 'queue' | 'batches' | 'defects') => setTab(value)}>
            <Tab value="queue" label={t('ins:claims.tab_queue')} data-testid="ins-tab-queue" />
            <Tab value="batches" label={t('ins:claims.tab_batches')} data-testid="ins-tab-batches" />
            <Tab value="defects" label={t('ins:claims.tab_defects')} data-testid="ins-tab-defects" />
          </Tabs>
        </Stack>
      }
    >
      {tab === 'queue' ? (
        <QueueTab insurerId={insurerId} canManage={canManage} />
      ) : tab === 'batches' ? (
        <BatchesTab insurerId={insurerId} canManage={canManage} canSubmit={access.can('ins.claim.submit') && write.canWrite} />
      ) : (
        <DefectsTab />
      )}
    </ListPage>
  );
}

/** صف آماده‌سازی: اقلام مطالبه با صافی وضعیت و «در خطر انقضا» و دکمه کنترل کیفیت (US-INS-060، 062). */
function QueueTab({ insurerId, canManage }: { insurerId: string; canManage: boolean }) {
  const { t } = useTranslation('ins');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [status, setStatus] = useState<ClaimStatus | ''>('');
  const [atRisk, setAtRisk] = useState(false);
  const [outcome, setOutcome] = useState<InsClaimQualityView | null>(null);
  const [returning, setReturning] = useState<InsClaimItemView | null>(null);
  const filter = `${insurerId}|${status}|${atRisk}`;

  const items = useQuery({
    queryKey: claimKeys.items(session?.tenantId, filter),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/insurance/claim-items', {
          params: {
            query: {
              insurerId: insurerId === '' ? undefined : insurerId,
              status: status === '' ? undefined : status,
              atRisk: atRisk ? true : undefined,
            },
          },
        }),
      ),
  });

  const check = useMutation({
    mutationFn: (claimItemIds: string[]) =>
      unwrap(
        tenantApi.POST('/api/v1/insurance/claim-items:quality-check', {
          body: { claimItemIds },
        }),
      ),
    onSuccess: async (result) => {
      setOutcome(result);
      await queryClient.invalidateQueries({ queryKey: ['ins', 'claim-items'] });
    },
  });

  const build = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/insurance/claim-batches', {
          headers: { 'Idempotency-Key': idempotency.current() },
          body: { insurerId: insurerId === '' ? null : insurerId, branchId: null, periodDate: null },
        }),
      ),
    onSuccess: async () => {
      idempotency.settle();
      await queryClient.invalidateQueries({ queryKey: ['ins', 'claim-items'] });
      await queryClient.invalidateQueries({ queryKey: ['ins', 'claim-batches'] });
    },
    onError: (error) => idempotency.settle(error),
  });

  const columns = itemColumns();

  if (canManage) {
    columns.push({
      id: 'actions',
      headerKey: 'ins:claims.columns.actions',
      cell: (row) =>
        row.status === 'Submitted' ? (
          <Button size="small" onClick={() => setReturning(row)} data-testid={`ins-claim-return-${row.claimItemId}`}>
            {t('ins:claims.return_item')}
          </Button>
        ) : null,
    });
  }

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
        <TextField
          select
          size="small"
          label={t('ins:claims.status')}
          value={status}
          onChange={(event) => setStatus(event.target.value as ClaimStatus | '')}
          sx={{ minWidth: 180 }}
          data-testid="ins-claims-status"
        >
          <MenuItem value="">{t('ins:claims.all')}</MenuItem>
          {CLAIM_STATUSES.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`ins:claim_status.${value}`)}
            </MenuItem>
          ))}
        </TextField>
        <Button
          size="small"
          variant={atRisk ? 'contained' : 'outlined'}
          onClick={() => setAtRisk((value) => !value)}
          data-testid="ins-claims-at-risk"
        >
          {t('ins:claims.at_risk_only')}
        </Button>
        {canManage ? (
          <>
            <Button
              size="small"
              variant="outlined"
              disabled={check.isPending || (items.data ?? []).length === 0}
              onClick={() => check.mutate((items.data ?? []).map((row) => row.claimItemId))}
              data-testid="ins-claims-quality-check"
            >
              {t('ins:claims.quality_check')}
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={build.isPending}
              onClick={() => build.mutate()}
              data-testid="ins-claims-build-batches"
            >
              {t('ins:claims.build_batches')}
            </Button>
          </>
        ) : null}
      </Stack>
      {outcome ? (
        <Alert severity="info" data-testid="ins-claims-quality-result">
          {t('ins:claims.quality_result', {
            checked: faDigits(Number(outcome.checked)),
            ready: faDigits(Number(outcome.ready)),
            needsFix: faDigits(Number(outcome.needsFix)),
          })}
        </Alert>
      ) : null}
      <DataTable
        captionKey="ins:claims.caption"
        columns={columns}
        rows={items.data}
        getRowId={(row) => row.claimItemId}
        isLoading={items.isPending}
        error={items.error}
        onRetry={() => void items.refetch()}
        emptyTitleKey="ins:claims.empty"
      />
      {returning && <ReturnDialog item={returning} onClose={() => setReturning(null)} />}
    </Stack>
  );
}

/** دسته‌های دوره با ارسال، مغایرت‌گیری و بستن (US-INS-061، 063). */
function BatchesTab({
  insurerId,
  canManage,
  canSubmit,
}: {
  insurerId: string;
  canManage: boolean;
  canSubmit: boolean;
}) {
  const { t } = useTranslation('ins');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState<InsClaimBatchView | null>(null);
  const [result, setResult] = useState<number | null>(null);

  const batches = useQuery({
    queryKey: claimKeys.batches(session?.tenantId, insurerId),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/insurance/claim-batches', {
          params: { query: { insurerId: insurerId === '' ? undefined : insurerId } },
        }),
      ),
  });

  const transition = useMutation({
    mutationFn: ({ batchId, action }: { batchId: string; action: 'reconcile' | 'close' }) =>
      action === 'reconcile'
        ? unwrap(tenantApi.POST('/api/v1/insurance/claim-batches/{id}:reconcile', { params: { path: { id: batchId } } }))
        : unwrap(tenantApi.POST('/api/v1/insurance/claim-batches/{id}:close', { params: { path: { id: batchId } } })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ins', 'claim-batches'] }),
  });

  const columns: DataTableColumn<InsClaimBatchView>[] = [
    { id: 'insurer', headerKey: 'ins:claims.columns.insurer', cell: (row) => row.insurerName },
    { id: 'period', headerKey: 'ins:claims.columns.period', cell: (row) => `${row.periodLabel}${row.isSupplementary ? ` (${faDigits(Number(row.sequence))})` : ''}` },
    {
      id: 'status',
      headerKey: 'ins:claims.columns.status',
      cell: (row) => <StatusChip tone={batchTone(row.status)} labelKey={`ins:claim_batch_status.${row.status}`} />,
    },
    { id: 'method', headerKey: 'ins:claims.columns.method', cell: (row) => t(`ins:submission_method.${row.method}`) },
    { id: 'items', headerKey: 'ins:claims.columns.items', cell: (row) => faDigits(Number(row.itemCount)), align: 'right' },
    { id: 'claimed', headerKey: 'ins:claims.columns.claimed', cell: (row) => <Money amountMinor={Number(row.claimed.amount)} />, align: 'right' },
    {
      id: 'risk',
      headerKey: 'ins:claims.columns.risk',
      cell: (row) =>
        Number(row.riskItemCount) > 0 ? (
          <span data-testid={`ins-batch-risk-${row.batchId}`}>
            <StatusChip tone="error" labelKey="ins:claims.columns.risk" />
          </span>
        ) : (
          '—'
        ),
      align: 'right',
    },
    { id: 'tracking', headerKey: 'ins:claims.columns.tracking', cell: (row) => row.trackingNumber ?? row.letterNumber ?? '—' },
    {
      id: 'actions',
      headerKey: 'ins:claims.columns.actions',
      cell: (row) => (
        <Stack direction="row" spacing={1}>
          {canSubmit && (row.status === 'Draft' || row.status === 'QualityChecked') ? (
            <Button size="small" onClick={() => setSubmitting(row)} data-testid={`ins-batch-submit-${row.batchId}`}>
              {t('ins:claims.submit')}
            </Button>
          ) : null}
          {canManage && row.status === 'Submitted' ? (
            <Button
              size="small"
              onClick={() => transition.mutate({ batchId: row.batchId, action: 'reconcile' })}
              data-testid={`ins-batch-reconcile-${row.batchId}`}
            >
              {t('ins:claims.reconcile')}
            </Button>
          ) : null}
          {canManage && row.status === 'Reconciled' ? (
            <Button
              size="small"
              onClick={() => transition.mutate({ batchId: row.batchId, action: 'close' })}
              data-testid={`ins-batch-close-${row.batchId}`}
            >
              {t('ins:claims.close')}
            </Button>
          ) : null}
        </Stack>
      ),
    },
  ];

  return (
    <Stack spacing={2}>
      {result !== null ? (
        <Alert severity="success" data-testid="ins-batch-submitted">
          {t('ins:claims.submitted_result', { returned: faDigits(result) })}
        </Alert>
      ) : null}
      <DataTable
        captionKey="ins:claims.batches_caption"
        columns={columns}
        rows={batches.data}
        getRowId={(row) => row.batchId}
        isLoading={batches.isPending}
        error={batches.error}
        onRetry={() => void batches.refetch()}
        emptyTitleKey="ins:claims.batches_empty"
      />
      {submitting && (
        <SubmitDialog
          batch={submitting}
          onClose={() => setSubmitting(null)}
          onDone={(returned) => {
            setResult(returned);
            setSubmitting(null);
          }}
        />
      )}
    </Stack>
  );
}

/** صف نواقص به تفکیک مسئول رفع (AC-INS-062.2؛ همان صفی که CLN در کارهای معوق پزشک نشان می‌دهد). */
function DefectsTab() {
  const { t } = useTranslation('ins');
  const { tenantApi, session } = useTenantSession();
  const [owner, setOwner] = useState<InsClaimDefectOwner>('InsuranceClerk');

  const defects = useQuery({
    queryKey: claimKeys.defects(session?.tenantId, owner),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/insurance/claim-defects', { params: { query: { owner } } })),
  });

  const columns: DataTableColumn<InsClaimDefectInfo>[] = [
    {
      id: 'check',
      headerKey: 'ins:claims.columns.check',
      cell: (row) => <DefectOwner check={row.check} owner={row.owner} />,
    },
    { id: 'owner', headerKey: 'ins:claims.columns.owner', cell: (row) => t(`ins:defect_owner.${row.owner}`) },
    { id: 'service_date', headerKey: 'ins:claims.columns.service_date', cell: (row) => <JalaliDate value={row.serviceDate} /> },
    {
      id: 'deadline',
      headerKey: 'ins:claims.columns.deadline',
      cell: (row) =>
        row.deadlineOn ? (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <JalaliDate value={row.deadlineOn} />
            {row.atRisk ? <StatusChip tone="error" labelKey="ins:claims.at_risk" /> : null}
          </Stack>
        ) : (
          '—'
        ),
    },
  ];

  return (
    <Stack spacing={2}>
      <TextField
        select
        size="small"
        label={t('ins:claims.owner')}
        value={owner}
        onChange={(event) => setOwner(event.target.value as InsClaimDefectOwner)}
        sx={{ minWidth: 200 }}
        data-testid="ins-defects-owner"
      >
        {DEFECT_OWNERS.map((value) => (
          <MenuItem key={value} value={value}>
            {t(`ins:defect_owner.${value}`)}
          </MenuItem>
        ))}
      </TextField>
      <DataTable
        captionKey="ins:claims.defects_caption"
        columns={columns}
        rows={defects.data}
        getRowId={(row) => `${row.claimItemId}-${row.check}`}
        isLoading={defects.isPending}
        error={defects.error}
        onRetry={() => void defects.refetch()}
        emptyTitleKey="ins:claims.defects_empty"
      />
    </Stack>
  );
}

const submitSchema = z.object({
  acknowledgeRisk: z.boolean(),
  letterNumber: z.string().max(64),
  letterDate: z.string(),
});

type SubmitForm = z.input<typeof submitSchema>;

/** ارسال دسته (AC-INS-063.1): تأیید «ارسال با ریسک» برای اقلام دارای نقص (BR-INS-062). */
function SubmitDialog({
  batch,
  onClose,
  onDone,
}: {
  batch: InsClaimBatchView;
  onClose: () => void;
  onDone: (returned: number) => void;
}) {
  const { t } = useTranslation('ins');
  const { tenantApi } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const form = useForm<SubmitForm>({
    resolver: zodResolver(submitSchema),
    defaultValues: { acknowledgeRisk: false, letterNumber: '', letterDate: '' },
  });

  const submit = useMutation({
    mutationFn: (values: SubmitForm) =>
      unwrap(
        tenantApi.POST('/api/v1/insurance/claim-batches/{id}:submit', {
          params: { path: { id: batch.batchId } },
          headers: { 'Idempotency-Key': idempotency.current() },
          body: {
            acknowledgeRisk: values.acknowledgeRisk,
            letterNumber: values.letterNumber === '' ? null : values.letterNumber,
            letterDate: values.letterDate === '' ? null : values.letterDate,
          },
        }),
      ),
    onSuccess: async (result) => {
      idempotency.settle();
      await queryClient.invalidateQueries({ queryKey: ['ins', 'claim-batches'] });
      await queryClient.invalidateQueries({ queryKey: ['ins', 'claim-items'] });
      onDone(Number(result.returnedItemCount));
    },
    onError: (error) => {
      idempotency.settle(error);
      const applied = applyServerErrors(error, form.setError, { fields: ['letterNumber'] });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      screenId={SCR_STAFF_853}
      titleKey="ins:claims.submit_title"
      onSubmit={form.handleSubmit((values) => submit.mutate(values))}
      onClose={onClose}
      isDirty={form.formState.isDirty}
      isSubmitting={submit.isPending}
      submitError={submitError}
      submitKey="ins:claims.submit"
    >
      {Number(batch.riskItemCount) > 0 ? (
        <Alert severity="warning" data-testid="ins-submit-risk-hint">
          {t('ins:claims.submit_risk_hint', { count: faDigits(Number(batch.riskItemCount)) })}
        </Alert>
      ) : null}
      <CheckboxRHF control={form.control} name="acknowledgeRisk" labelKey="ins:claims.submit_risk" />
      <TextFieldRHF control={form.control} name="letterNumber" labelKey="ins:claims.letter_number" fullWidth />
      <JalaliDatePickerRHF control={form.control} name="letterDate" labelKey="ins:claims.letter_date" />
    </FormDialog>
  );
}

const returnSchema = z.object({ reasonCode: z.string().min(1, 'common:validation.required') });
type ReturnForm = z.input<typeof returnSchema>;

/** ثبت برگشتی قلم از بیمه‌گر (AC-INS-064.1). */
function ReturnDialog({ item, onClose }: { item: InsClaimItemView; onClose: () => void }) {
  const { tenantApi } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const form = useForm<ReturnForm>({
    resolver: zodResolver(returnSchema),
    defaultValues: { reasonCode: 'MissingDocument' },
  });

  const save = useMutation({
    mutationFn: (values: ReturnForm) =>
      unwrap(
        tenantApi.POST('/api/v1/insurance/claim-items/{id}:return', {
          params: { path: { id: item.claimItemId } },
          headers: { 'Idempotency-Key': idempotency.current() },
          body: { reasonCode: values.reasonCode as DeductionReason },
        }),
      ),
    onSuccess: async () => {
      idempotency.settle();
      await queryClient.invalidateQueries({ queryKey: ['ins', 'claim-items'] });
      onClose();
    },
    onError: (error) => {
      idempotency.settle(error);
      setSubmitError(error);
    },
  });

  return (
    <FormDialog
      open
      screenId={SCR_STAFF_853}
      titleKey="ins:claims.return_title"
      onSubmit={form.handleSubmit((values) => save.mutate(values))}
      onClose={onClose}
      isDirty={form.formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <SelectRHF
        control={form.control}
        name="reasonCode"
        labelKey="ins:claims.return_reason"
        options={DEDUCTION_REASONS.map((value) => ({ value, labelKey: `ins:deduction_reason.${value}` }))}
        fullWidth
      />
    </FormDialog>
  );
}

/** ستون‌های مشترک جدول اقلام مطالبه (صف آماده‌سازی و صفحه کسورات). */
export function itemColumns(): DataTableColumn<InsClaimItemView>[] {
  return [
    { id: 'patient', headerKey: 'ins:claims.columns.patient', cell: (row) => row.patientName },
    { id: 'service', headerKey: 'ins:claims.columns.service', cell: (row) => row.serviceName },
    { id: 'service_date', headerKey: 'ins:claims.columns.service_date', cell: (row) => <JalaliDate value={row.serviceDate} /> },
    { id: 'insurer', headerKey: 'ins:claims.columns.insurer', cell: (row) => row.insurerName },
    { id: 'claimed', headerKey: 'ins:claims.columns.claimed', cell: (row) => <Money amountMinor={Number(row.claimed.amount)} />, align: 'right' },
    { id: 'deducted', headerKey: 'ins:claims.columns.deducted', cell: (row) => <Money amountMinor={Number(row.deducted.amount)} />, align: 'right' },
    { id: 'open', headerKey: 'ins:claims.columns.open', cell: (row) => <Money amountMinor={Number(row.open.amount)} />, align: 'right' },
    {
      id: 'status',
      headerKey: 'ins:claims.columns.status',
      cell: (row) => (
        <Stack spacing={0.5}>
          <StatusChip tone={claimTone(row.status)} labelKey={`ins:claim_status.${row.status}`} />
          {row.atRisk ? <StatusChip tone="error" labelKey="ins:claims.at_risk" /> : null}
        </Stack>
      ),
    },
    {
      id: 'defects',
      headerKey: 'ins:claims.columns.defects',
      cell: (row) => <ClaimDefects defects={row.defects} />,
    },
    {
      id: 'deadline',
      headerKey: 'ins:claims.columns.deadline',
      cell: (row) =>
        row.submissionDeadlineOn ? (
          <Typography variant="body2">
            <JalaliDate value={row.submissionDeadlineOn} />
          </Typography>
        ) : (
          '—'
        ),
    },
  ];
}
